// Import the functions you need from the SDKs you need
import { initializeApp, FirebaseError } from "firebase/app"
import {
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from "firebase/messaging"
import { pushDeviceApi } from "store/api"
import {
  SW_VERSION,
  firebaseConfig,
  vapidKey,
} from "initializers/firebase-config"
import { deleteToken, Messaging } from "@firebase/messaging"
import { AxiosError } from "axios"
import log from "features/common/logging/logging"

// Initialize Firebase
const firebaseApp = initializeApp(firebaseConfig)

// Save the timestamp when the device registration was last confirmed to the API
const cookieName = "suus_last_registration_pinged_at"

export async function enablePush(repairPush?: boolean) {
  log(SW_VERSION + " Enabling Push Notfications...")

  // Let firebase check if the browser supports all necessary APIs
  const hasFirebaseMessagingSupport = await isSupported()
  if (!hasFirebaseMessagingSupport) {
    alert("Firebase Cloud Messaging is not supported by this browser.")
    throw new Error("Firebase Cloud Messaging is not supported.")
  }

  const firebaseMessaging = getMessaging(firebaseApp)

  const serviceWorkerRegistration =
    await navigator.serviceWorker.getRegistration()

  if (!serviceWorkerRegistration) {
    throw new Error("Service Worker not found!")
  }

  if (repairPush) {
    log("Deleting old token...")
    let token = null
    try {
      token = await getToken(firebaseMessaging, {
        vapidKey,
        serviceWorkerRegistration,
      })
      await deleteToken(firebaseMessaging)
    } catch (e) {
      if (e instanceof FirebaseError) {
        log("FCM error during deletion of device registration: " + e.code, e)
        if (token) {
          try {
            await pushDeviceApi.deletePushRegistration(token)
          } catch (e) {
            // Ignore 404 errors
            if (
              (e as AxiosError).isAxiosError &&
              (e as AxiosError).response.status === 404
            ) {
              log("Token was not present in database already, continuing...")
            } else {
              throw e
            }
          }
        }
      } else {
        log("Some error while deleting token!", e)
      }
    }
  }

  // getToken requests permission already, if not already granted
  const registrationId = await getToken(firebaseMessaging, {
    vapidKey,
    serviceWorkerRegistration,
  })
  log("Token available, sending to backend: " + registrationId)

  return await pushDeviceApi.createPushRegistration({
    registration_id: registrationId,
  })
}

export async function sendTestPush() {
  const serviceWorkerRegistration =
    await navigator.serviceWorker.getRegistration()

  if (serviceWorkerRegistration) {
    const token = await getToken(getMessaging(firebaseApp), {
      vapidKey,
      serviceWorkerRegistration,
    })
    return pushDeviceApi.sendTestNotification(token)
  } else {
    return Promise.reject("Service Worker not found!")
  }
}

export async function waitForTestMessage(
  messageId,
  timeout: number
): Promise<boolean> {
  const messaging = getMessaging(firebaseApp)
  return new Promise((resolve) => {
    function handleMessage(payload) {
      log("Awaited message received!", payload)
      if (payload.messageId) {
        clearTimeout(timeoutId)
        if (unsubscribe) {
          unsubscribe()
        }
        resolve(true)
      }
    }

    log("Waiting for message")
    const unsubscribe = onMessage(messaging, handleMessage)

    const timeoutId = setTimeout(() => {
      unsubscribe()
      resolve(false)
    }, timeout) // timeout after 10 seconds
  })
}

function getLastPingTimestamp() {
  const v = document.cookie.match("(^|;) ?" + cookieName + "=([^;]*)(;|$)")
  return v ? Number.parseInt(v[2]) : 0
}

function isTimeForNextPing() {
  const lastUpdated = getLastPingTimestamp()
  const oneHour = 60 * 60 * 1000
  const nextUpdate = new Date(lastUpdated + oneHour)
  return nextUpdate < new Date()
}

function updatePingCookie() {
  document.cookie = cookieName + "=" + Date.now().toString() + ";path=/;"
}

async function refreshDeviceRegistration(
  firebaseMessaging: Messaging,
  serviceWorkerRegistration: ServiceWorkerRegistration
) {
  if (isTimeForNextPing() && Notification.permission === "granted") {
    const currentToken = await getToken(firebaseMessaging, {
      vapidKey,
      serviceWorkerRegistration,
    })
    if (currentToken) {
      try {
        const response = await pushDeviceApi.updatePushRegistration({
          registration_id: currentToken,
        })
        console.info("PushRegistration updated.")
        updatePingCookie()
      } catch (e) {
        // Only in case of 404 repair the registration. If the promise fails for other reasons, do not repair it.
        if (
          (e as AxiosError).isAxiosError &&
          (e as AxiosError).response.status === 404
        ) {
          // Show permission request UI
          console.warn("Token has ceased, repairing registration.")
          await enablePush(true)
          log("Ready!")
        } else {
          throw e
        }
      }
    } else {
      console.warn("No token yet available")
    }
  }
}

async function initRegistrationRefresh(sw: ServiceWorker) {
  log("The site is now controlled by a SW!")

  if (await isSupported()) {
    // Get a Firebase Messaging instance.
    const firebaseMessaging = getMessaging(firebaseApp)

    // Export the function to the window object, so it can be called from the UI
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.enablePush = enablePush

    // Refresh the device registration, so the backend knows the client is still active
    // Send a ping to the backend, updating the token activity timestamp.
    const serviceWorkerRegistration =
      await navigator.serviceWorker.getRegistration()

    if (!serviceWorkerRegistration) {
      throw new Error("This client is not controlled by a service worker!")
    }
    /* Disable because handler is not set
        handleForegroundNotifications(
          firebaseMessaging,
          serviceWorkerRegistration
        )*/
    await refreshDeviceRegistration(
      firebaseMessaging,
      serviceWorkerRegistration
    )
  } else {
    console.warn("Push notifications are not supported by this browser.")
  }
}

export { firebaseApp, initRegistrationRefresh }
